import { Link } from 'react-router-dom';
import { COMPANY, LegalDoc, ToConfirm, type LegalSection } from '../components/LegalDoc';

/**
 * The privacy policy.
 *
 * Written from what the product actually does rather than from a template.
 * Every factual claim below was checked against the code: there are no
 * analytics scripts, the browser stores exactly two things, isolation is
 * enforced by row-level security in the database, and passwords are argon2id
 * hashes. A policy that describes a system nobody built is worse than none,
 * because it is the document a regulator will hold you to.
 *
 * The section that matters most is §2. When a landlord records a tenant, the
 * LANDLORD is the data controller and NyumbaSuite is the processor. Most SaaS
 * privacy policies blur that, and blurring it is how a platform ends up
 * answering for decisions it never made — or a landlord assumes the platform
 * is carrying an obligation that is actually theirs.
 */

const sections: LegalSection[] = [
  {
    id: 'who-we-are',
    heading: 'Who this policy is from',
    body: (
      <>
        <p>
          {COMPANY.tradingName} is property management software for landlords and agents in
          Kenya and East Africa. This policy explains what personal data the service
          handles, why, and what you can do about it.
        </p>
        <p>
          The service is operated by <ToConfirm>{COMPANY.legalName}</ToConfirm>,{' '}
          <ToConfirm>{COMPANY.registration}</ToConfirm>, of{' '}
          <ToConfirm>{COMPANY.address}</ToConfirm>.
        </p>
        <p>
          It applies to the NyumbaSuite website, the landlord portal, and the tenant
          confirmation links we send. It is written to meet the Data Protection Act,
          2019 (Kenya).
        </p>
      </>
    ),
  },
  {
    id: 'two-roles',
    heading: 'The distinction that matters: whose data is whose',
    body: (
      <>
        <p>
          NyumbaSuite handles two different kinds of personal data, and the law treats
          them differently. Getting this the wrong way round is the most common mistake
          in policies like this one, so it is stated first.
        </p>
        <div className="legal-two">
          <div>
            <h3>Your data — we are the controller</h3>
            <p>
              The account you open with us: your name, email, phone number, the plan you
              are on, and what you do inside the product. We decide why and how this is
              handled, so we are the data controller and answerable for it.
            </p>
          </div>
          <div>
            <h3>Your tenants&rsquo; data — you are the controller</h3>
            <p>
              Everything you record about the people renting from you: names, phone
              numbers, ID numbers, tenancy terms, payments, inspection photographs.{' '}
              <strong>You decide what to collect and why. You are the data controller
              for it; we are your processor.</strong> We act on your instructions, we do
              not use it for our own purposes, and we do not sell it or share it with
              anyone else.
            </p>
          </div>
        </div>
        <p>
          The practical consequence: if a tenant asks to see, correct or delete what is
          held about them, that request belongs to the landlord who recorded it, not to
          us. We will help you answer it and we will never obstruct one, but we cannot
          make that decision on your behalf.
        </p>
      </>
    ),
  },
  {
    id: 'what-we-collect',
    heading: 'What is collected',
    body: (
      <>
        <h3>When you open an account</h3>
        <p>
          Your full name, email address, phone number, and a password. The password is
          never stored — what we keep is an argon2id hash of it, which cannot be turned
          back into the password you chose. We also record your business name, country
          and currency, and the plan you select.
        </p>

        <h3>What you record in the product</h3>
        <p>
          Properties and units; tenants and their contact details, ID numbers,
          emergency contacts and employment notes where you choose to record them;
          tenancies and their terms; rent charges, payments, receipts and invoices;
          arrears; maintenance jobs; expenses; utility readings; documents you upload;
          inspection checklists and photographs; and listings you publish.
        </p>
        <p>
          You decide what goes in. NyumbaSuite does not require an ID number or an
          employment detail in order to work — those fields exist because landlords
          asked for them, and they stay empty if you leave them empty.
        </p>

        <h3>When you visit the public site</h3>
        <ul>
          <li>
            <strong>Enquiring about a listing:</strong> your name, phone number, email if
            you give one, and your message. This goes to the landlord advertising that
            unit, because that is the point of sending it.
          </li>
          <li>
            <strong>Requesting a demo or a quote:</strong> your name, contact details and
            what you told us about your portfolio.
          </li>
          <li>
            <strong>Sending us a story for the blog:</strong> what you wrote, your name and
            contact details, and your explicit consent to publish. Nothing is published
            without a person reviewing it first.
          </li>
          <li>
            <strong>Asking the assistant on the public site:</strong> the question you
            typed and the answer given. It has no access to any account and cannot look
            anything up about you.
          </li>
          <li>
            <strong>Your IP address</strong> is recorded with the forms above, and used
            only to limit abuse of them.
          </li>
        </ul>

        <h3>Tenant confirmation links</h3>
        <p>
          When a landlord completes an inspection, we generate a one-off link so the
          tenant can review and accept or dispute it without needing an account. Opening
          it records nothing about the person beyond their answer and the time they gave
          it. The link works once and expires after fourteen days.
        </p>
      </>
    ),
  },
  {
    id: 'why',
    heading: 'Why, and on what lawful basis',
    body: (
      <>
        <ul>
          <li>
            <strong>To provide the service you asked for</strong> — performance of a
            contract. Running your account, keeping your records, producing your
            receipts and reports.
          </li>
          <li>
            <strong>To keep the service safe and working</strong> — legitimate interest.
            Rate limiting, audit logs, detecting misuse.
          </li>
          <li>
            <strong>To answer you</strong> — performance of a contract or your consent,
            depending on whether you are already a customer.
          </li>
          <li>
            <strong>To publish a story you sent us</strong> — your consent, given
            explicitly, and withdrawable.
          </li>
          <li>
            <strong>To meet legal obligations</strong> — where the law requires us to keep
            or produce something.
          </li>
        </ul>
        <p>
          We do not profile you, we do not make automated decisions that affect you, and
          we do not use your data or your tenants&rsquo; data to train any model.
        </p>
      </>
    ),
  },
  {
    id: 'who-can-see',
    heading: 'Who can see your records',
    body: (
      <>
        <p>
          Separation between accounts is enforced by the database itself, not by
          application code that could forget. Every table carrying account data has a
          row-level security policy on it, and the role the application connects as
          cannot bypass those policies. A query that omitted its filter would return
          nothing rather than someone else&rsquo;s records.
        </p>
        <p>
          <strong>Our own staff cannot browse your business records.</strong> The
          administration portal is deliberately built without access to tenants, rent,
          payments, invoices or documents — support can see who you are, what plan you
          are on and what has been done to your account, and no more. Where a support
          question genuinely requires more, it needs a time-boxed grant that is recorded
          in the audit trail.
        </p>
        <p>
          Inside your own account, you decide who sees what: property managers, finance
          and staff users each hold a different set of permissions, and a property
          manager can be limited to the properties assigned to them.
        </p>
      </>
    ),
  },
  {
    id: 'where-and-who',
    heading: 'Where your data is kept, and who else touches it',
    body: (
      <>
        <p>
          Your data is held in a managed PostgreSQL database and served from cloud
          infrastructure. Both are outside Kenya, so your data is transferred and stored
          abroad; we rely on the contractual protections our providers offer, and on
          your consent to that transfer where the Act requires it.
        </p>
        <table className="legal-table">
          <thead>
            <tr><th>Who</th><th>What they do</th><th>What they can see</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Neon</td>
              <td>Hosts the database</td>
              <td>Stores everything, encrypted in transit</td>
            </tr>
            <tr>
              <td>Render</td>
              <td>Runs the application</td>
              <td>Processes requests; keeps operational logs</td>
            </tr>
            <tr>
              <td>Email provider</td>
              <td>Delivers notifications and receipts</td>
              <td>The address and contents of messages we send</td>
            </tr>
            <tr>
              <td>Anthropic</td>
              <td>Powers the AI assistant, when enabled</td>
              <td>The question asked and the figures needed to answer it</td>
            </tr>
          </tbody>
        </table>
        <p>
          The assistant is worth a specific word. It has no standing access to anything:
          it can only run a fixed set of named lookups, chosen from the list your own
          permissions already allow, and it never receives a record you could not have
          opened on screen yourself. Nothing it sees is used to train anything.
        </p>
        <p>
          <strong>We do not sell personal data, and we never will.</strong> We do not
          share it with advertisers or data brokers.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    heading: 'Cookies and tracking',
    body: (
      <>
        <p>
          There is very little to declare here, which is deliberate.
        </p>
        <p>
          <strong>NyumbaSuite runs no analytics, no advertising pixels and no
          third-party trackers.</strong> Nobody is watching which articles you read or
          which pages you visit. That is why the site has never shown you a cookie
          banner: there is nothing to ask you to consent to.
        </p>
        <p>Your browser stores exactly two things, both of them locally:</p>
        <ul>
          <li>your sign-in session, so you are not asked to log in on every page;</li>
          <li>whether you prefer the light or dark theme.</li>
        </ul>
        <p>
          Clearing your browser storage removes both. Neither is sent to anyone but us,
          and the theme preference is not sent anywhere at all.
        </p>
      </>
    ),
  },
  {
    id: 'how-long',
    heading: 'How long it is kept',
    body: (
      <>
        <p>
          While your account is open, your records are kept for as long as you want
          them — that is what a property management system is for, and a rent history
          you cannot produce three years later is of no use to anyone.
        </p>
        <p>
          Deleting a record inside the product marks it deleted and removes it from
          every screen and report, while keeping it recoverable for a short period
          against mistakes. Closing your account starts the removal of your data. Before
          you close it, you can export everything you have recorded as spreadsheets from{' '}
          <Link to="/data">Import &amp; export</Link>. How to close an account, from the
          app or without it, is set out on <Link to="/delete-account">Delete your account</Link>.
        </p>
        <p>
          Some records are kept longer where the law requires it — financial records in
          particular. Audit logs, which record who did what and when, are kept because
          their whole purpose is to be there afterwards.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    heading: 'Your rights',
    body: (
      <>
        <p>Under the Data Protection Act, 2019 you have the right to:</p>
        <ul>
          <li>be told how your data is used — which is what this document is for;</li>
          <li>ask for a copy of what is held about you;</li>
          <li>have anything inaccurate corrected;</li>
          <li>ask for deletion, where there is no lasting reason to keep it;</li>
          <li>object to particular uses, or withdraw consent you gave;</li>
          <li>receive your data in a portable form — the export tool does this today.</li>
        </ul>
        <p>
          Write to <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>{' '}
          and we will respond within the time the Act allows. We do not charge for this.
        </p>
        <p>
          <strong>If you are a tenant</strong> and the data concerns your tenancy, the
          landlord or agent who recorded it is the controller and the request belongs
          with them. Tell us and we will point you to the right party and make sure they
          can act on it.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'How it is protected',
    body: (
      <>
        <ul>
          <li>Passwords are stored as argon2id hashes, never as text.</li>
          <li>Traffic is encrypted in transit.</li>
          <li>Account separation is enforced in the database, not only in code.</li>
          <li>Sessions expire, and repeated failed sign-ins lock an account.</li>
          <li>Actions that change data are written to an audit trail.</li>
        </ul>
        <p>
          No system is beyond compromise. If a breach occurs that is likely to harm you,
          we will tell you and notify the Data Commissioner as the Act requires, rather
          than waiting to be asked.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    heading: 'Children',
    body: (
      <p>
        NyumbaSuite is a business tool and is not directed at children. We do not
        knowingly collect data from anyone under eighteen. If a child&rsquo;s data has
        reached us, tell us and we will remove it.
      </p>
    ),
  },
  {
    id: 'odpc',
    heading: 'Complaints and the Data Commissioner',
    body: (
      <>
        <p>
          If something about how your data is handled troubles you, please raise it with
          us first at{' '}
          <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>. We would
          rather hear it and fix it.
        </p>
        <p>
          You also have the right to complain directly to the{' '}
          <a href="https://www.odpc.go.ke" target="_blank" rel="noreferrer">
            Office of the Data Protection Commissioner
          </a>{' '}
          in Kenya, whether or not you have raised it with us. Using us first is not a
          condition of that right.
        </p>
        <p>Our registration with the ODPC: <ToConfirm>{COMPANY.odpcRegistration}</ToConfirm>.</p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: (
      <p>
        When this policy changes we will update the effective date at the top. If a
        change materially affects how your data is used, we will tell account holders
        directly rather than relying on you to notice.
      </p>
    ),
  },
];

export function Privacy() {
  return (
    <LegalDoc
      title="Privacy Policy"
      lede="What NyumbaSuite does with personal data — yours, and the data your tenants entrust to you."
      effectiveDate="21 August 2026"
      canonicalPath="/privacy"
      draft
      sections={sections}
    />
  );
}
